import React from "react";

/**
 * Wraps the app so that a JS error in any child renders a friendly recovery UI
 * instead of a blank white screen. Only errors bubble here — network errors are
 * already caught by axios/toast.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Unexpected error" };
  }

  componentDidCatch(error, info) {
    // Best-effort: log to PostHog if it's on the page.
    if (typeof window !== "undefined" && window.posthog) {
      try { window.posthog.captureException(error, { info }); } catch (e) { /* ignore */ }
    }
    // Keep the raw error in the console for support.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  reset = () => this.setState({ hasError: false, message: "" });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="error-boundary">
        <div className="max-w-md text-center">
          <div className="text-6xl">🧹</div>
          <h1 className="font-display text-3xl font-bold text-primary mt-4">Something went sideways</h1>
          <p className="text-sm text-muted-foreground mt-2">We hit an unexpected error. Try refreshing the page — if it keeps happening, ping <a className="text-secondary underline" href="mailto:support@quadscrub.com">support@quadscrub.com</a>.</p>
          <div className="mt-6 flex gap-2 justify-center">
            <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-md bg-primary text-primary-foreground font-medium">Refresh</button>
            <a href="/" onClick={this.reset} className="px-4 py-2 rounded-md border border-border font-medium">Home</a>
          </div>
          {process.env.NODE_ENV !== "production" && (
            <div className="mt-6 text-left text-xs font-mono bg-muted p-3 rounded overflow-x-auto">{this.state.message}</div>
          )}
        </div>
      </div>
    );
  }
}
